// -----------------------------------------------------------------------------
// <copyright file="http.service.ts" company="ecoInsight, Inc.">
// Copyright © ecoInsight, Inc. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import {
  BASE_API_URL,
  PENDO_BASE_API_URL,
  SpecialCharacters,
  SpecialCharactersHexValue,
} from '../constants/global';

@Injectable({ providedIn: 'root' })
export class HttpHelperService {
  private http = inject(HttpClient);

  public post(context: string, body: string): Observable<any> {
    return this.http.post(this.getUrl(context), body);
  }
  public postPendo(context: string, body: string): Observable<any> {
    return this.http.post(PENDO_BASE_API_URL + context, body);
  }

  public get(context: string): Observable<any> {
    const url = this.getUrl(context);
    if (!this.isTrustedUrl(url)) {
      throw new Error('Untrusted URL');
    }
    return this.http.get(url);
  }

  private isTrustedUrl(url: string): boolean {
    try {
      // const parsedUrl = new URL(url);
      const trustedDomains = [BASE_API_URL];

      return trustedDomains.some((trusted) => url.startsWith(trusted));
    } catch (e) {
      return false; // invalid URL format
    }
  }

  public getPendo(context: string): Observable<any> {
    const url = this.getPendoUrl(context);
    return this.http.get(url);
  }

  public put(context: string, body: string): Observable<any> {
    return this.http.put(this.getUrl(context), body);
  }

  public delete(context: string): Observable<any> {
    return this.http.delete(this.getUrl(context));
  }
  public patch(context: string, body: string): Observable<any> {
    return this.http.patch(this.getUrl(context), body);
  }

  private getUrl(contextUrl: string): any {
    let url = '';
    if (contextUrl.indexOf(BASE_API_URL) === -1) {
      url = BASE_API_URL + contextUrl;
      url += (url.indexOf('?') !== -1 ? '&t=' : '?t=') + Date.now();
    } else {
      url = contextUrl;
    }
    return url;
  }
  private getPendoUrl(contextUrl: string): any {
    let url = '';
    if (contextUrl.indexOf(PENDO_BASE_API_URL) === -1) {
      url = PENDO_BASE_API_URL + contextUrl;
      url += (url.indexOf('?') !== -1 ? '&t=' : '?t=') + Date.now();
    } else {
      url = contextUrl;
    }
    return url;
  }

  public getFile(context: string): Observable<any> {
    const newURL = this.getUrl(context);
    // newURL += (newURL.indexOf('?') !== -1 ? '&t=' : '?t=') + Date.now();
    return this.http.get(newURL, { responseType: 'blob' });
  }

  public getFilePost(context: string, pl: any): Observable<any> {
    const newURL = this.getUrl(context);
    // newURL += (newURL.indexOf('?') !== -1 ? '&t=' : '?t=') + Date.now();
    return this.http.post(newURL, pl, { responseType: 'blob' });
  }

  public odataSplCharacterEncoder(string: any) {
    let str = string;
    if (str == null) {
      return str;
    }
    if (str.toString().indexOf(SpecialCharacters.HASH) > -1) {
      str = str.replace(SpecialCharacters.HASH, SpecialCharactersHexValue.HASH);
    }
    if (str.toString().indexOf(SpecialCharacters.DOLLAR) > -1) {
      str = str.replace(SpecialCharacters.DOLLAR, SpecialCharactersHexValue.DOLLAR);
    }
    if (str.toString().indexOf(SpecialCharacters.AMPERSAND) > -1) {
      str = str.replace(SpecialCharacters.AMPERSAND, SpecialCharactersHexValue.AMPERSAND);
    }
    if (str.toString().indexOf(SpecialCharacters.PLUS) > -1) {
      str = str.replace(SpecialCharacters.PLUS, SpecialCharactersHexValue.PLUS);
    }
    if (str.toString().indexOf(SpecialCharacters.COMMA) > -1) {
      str = str.replace(SpecialCharacters.COMMA, SpecialCharactersHexValue.COMMA);
    }
    if (str.toString().indexOf(SpecialCharacters.FORWARD_SLASH) > -1) {
      str = str.replace(SpecialCharacters.FORWARD_SLASH, SpecialCharactersHexValue.FORWARD_SLASH);
    }
    if (str.toString().indexOf(SpecialCharacters.COLON) > -1) {
      str = str.replace(SpecialCharacters.COLON, SpecialCharactersHexValue.COLON);
    }
    if (str.toString().indexOf(SpecialCharacters.SEMI_COLON) > -1) {
      str = str.replace(SpecialCharacters.SEMI_COLON, SpecialCharactersHexValue.SEMI_COLON);
    }
    if (str.toString().indexOf(SpecialCharacters.EQUALS) > -1) {
      str = str.replace(SpecialCharacters.EQUALS, SpecialCharactersHexValue.EQUALS);
    }
    if (str.toString().indexOf(SpecialCharacters.QUESTION_MARK) > -1) {
      str = str.replace(SpecialCharacters.QUESTION_MARK, SpecialCharactersHexValue.QUESTION_MARK);
    }
    if (str.toString().indexOf(SpecialCharacters.AT_SYMBOL) > -1) {
      str = str.replace(SpecialCharacters.AT_SYMBOL, SpecialCharactersHexValue.AT_SYMBOL);
    }
    if (str.toString().indexOf(SpecialCharacters.QUOTES) > -1) {
      str = str.replace(SpecialCharacters.QUOTES, SpecialCharactersHexValue.QUOTES);
    }
    if (str.toString().indexOf(SpecialCharacters.DOUBLEQUOTES) > -1) {
      str = str.replace(SpecialCharacters.DOUBLEQUOTES, SpecialCharactersHexValue.DOUBLEQUOTES);
    }
    if (str.toString().indexOf(SpecialCharacters.PERIOD) > -1) {
      str = str.replace(SpecialCharacters.PERIOD, SpecialCharactersHexValue.PERIOD);
    }
    if (str.toString().indexOf(SpecialCharacters.APN) > -1) {
      str = str.replace(SpecialCharacters.APN, SpecialCharactersHexValue.APN);
    }
    if (str.toString().indexOf(SpecialCharacters.ASTERISK) > -1) {
      str = str.replace(SpecialCharacters.ASTERISK, SpecialCharactersHexValue.ASTERISK);
    }
    return str;
  }
}
